import { useState } from 'react';

import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { isBusyChat } from './chatMessages';
import type { ChatDecision } from './replyResults/ConfirmationCard';
import type { ChatApi } from './types';
import { useDecideChatMutation } from './useDecideChatMutation';

const BUSY_MESSAGE =
  'Finlay is still answering in another tab. Click again once that reply is in.';

const statusOf = (error: unknown) =>
  error && typeof error === 'object' && 'status' in error
    ? error.status
    : undefined;

/** Runs clicks on the changes Finlay proposed in an open chat, and says why one didn't go through. */
export const useChatDecide = (chat: ChatApi) => {
  const mutation = useDecideChatMutation();
  const [failure, setFailure] = useState<string | null>(null);

  const decide = (confirmationId: string, decision: ChatDecision) => {
    if (mutation.isPending) return;
    setFailure(null);
    mutation.mutate(
      { chatId: chat.id, confirmationId, decision },
      {
        onError: (error) =>
          setFailure(
            isBusyChat(error)
              ? BUSY_MESSAGE
              : statusOf(error) === 404
                ? "That change isn't in this chat anymore, so nothing was done."
                : actionErrorMessage('make that change', error),
          ),
      },
    );
  };

  return {
    decide,
    deciding: mutation.isPending
      ? {
          confirmationId: mutation.variables.confirmationId,
          decision: mutation.variables.decision,
        }
      : undefined,
    failure,
  };
};
